import React, { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { useAddLine, useRestorePosition } from 'src/api/projectVersionPositions'
import { useDeleteProjectPosition } from 'src/api/projectPosition'
import { IPosition, IPositionType } from 'src/generated-types'
import { refreshProjectData, withRemoved, withoutChanges } from './refreshProjectData'
import { RESTORE_ATTRIBUTE } from './revisionDrawingPainter'
import { ADD_LINE_ATTRIBUTE } from './revisionMapOverlay'
import { RevisionConfigure } from './useRevisionConfigure'

// Edits made by clicking on the map of a draft: bring a removed position back, add a mooring line, remove a position.
export const useRevisionMapActions = (customerId: EntityId, localityId: EntityId, projectId: EntityId) => {
  const { t: translate } = useTranslation(['projects', 'common'])
  const { mutateAsync: restorePosition } = useRestorePosition(customerId, localityId, projectId)
  const { mutateAsync: addLine } = useAddLine(customerId, localityId, projectId)
  const { mutateAsync: deletePosition } = useDeleteProjectPosition(customerId, projectId)

  const run = useCallback(
    async <T>(
      action: () => Promise<T>,
      successKey: string,
      failureKey: string,
      patch?: Parameters<typeof refreshProjectData>[1],
    ) => {
      try {
        const result = await action()
        toast.success(translate(successKey, { ns: 'projects' }))
        refreshProjectData(projectId, patch)
        return result
      } catch {
        toast.error(translate(failureKey, { ns: 'projects' }))
        return undefined
      }
    },
    [projectId, translate],
  )

  const restoreByName = useCallback(
    (name: string) =>
      run(() => restorePosition({ name }), 'revision-restored', 'revision-action-failed', withoutChanges([name], true)),
    [run, restorePosition],
  )

  // Resolves to the new line's id, so it can be selected for its anchor coordinates straight away.
  const addLineFrom = useCallback(
    async (cornerPositionId: number) => {
      const response = await run(() => addLine({ cornerPositionId }), 'revision-line-added', 'revision-add-line-failed')
      return (response?.data as { positionId?: number } | undefined)?.positionId
    },
    [run, addLine],
  )

  const removeById = useCallback(
    (position: IPosition) =>
      run(
        () => deletePosition(position.id),
        'revision-removed-restorable',
        'revision-action-failed',
        withRemoved([position.name ?? '']),
      ),
    [run, deletePosition],
  )

  return { restoreByName, addLineFrom, removeById }
}

interface UseRevisionMapClickOptions {
  revision?: RevisionConfigure
  customerId: EntityId
  localityId: EntityId
  projectId: EntityId
  allPositions: IPosition[]
  onLineAdded: (positionId: number) => void
}

// What a click on the draft's map does with the active tool; plain selection is left to the dialog.
export const useRevisionMapClick = ({
  revision,
  customerId,
  localityId,
  projectId,
  allPositions,
  onLineAdded,
}: UseRevisionMapClickOptions) => {
  const { t } = useTranslation(['projects'])
  const { restoreByName, addLineFrom, removeById } = useRevisionMapActions(customerId, localityId, projectId)
  const tool = revision?.view === 'after' ? revision.tool : 'select'

  return (event: React.MouseEvent, resolvePosition: () => number | null, selectPosition: (id: number) => void) => {
    const target = event.target as Element
    const marker = target.closest?.(`[${ADD_LINE_ATTRIBUTE}]`)
    if (revision && marker) {
      addLineFrom(Number(marker.getAttribute(ADD_LINE_ATTRIBUTE))).then(id => id && onLineAdded(id))
      return
    }
    const ghost = target.closest?.(`[${RESTORE_ATTRIBUTE}]`)
    if (revision && ghost) {
      const name = ghost.getAttribute(RESTORE_ATTRIBUTE) ?? ''
      if (tool === 'remove') restoreByName(name)
      else revision.toggleRemoved(name)
      return
    }
    const positionId = resolvePosition()
    if (positionId == null) return
    if (tool !== 'remove') {
      selectPosition(positionId)
      return
    }
    const position = allPositions.find(p => p.id === positionId)
    if (!position) return
    if (position.type === IPositionType.Corner) {
      toast.info(t('revision-corner-not-removable', { ns: 'projects' }))
      return
    }
    removeById(position)
  }
}
